import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const rarityColors = {
  0: '#B1C4D9',
  1: '#5F99D9',
  2: '#4C6AFF',
  3: '#8948FF',
  4: '#D32DE6',
  5: '#EB4C4C',
  6: '#FFD700',
};

function getCaseCount(rarity, confidence) {
  if (rarity <= 0 || rarity >= 1 || confidence <= 0 || confidence >= 1) {
    throw new Error("rarity and confidence must be between 0 and 1 (exclusive).");
  }
  const n = Math.log(1 - confidence) / Math.log(1 - rarity);
  return Math.ceil(n);
}

const SouvenirPackageDetailPage = () => {
  const { caseName } = useParams();
  const [caseData, setCaseData] = useState(null);
  const [caseCounts, setCaseCounts] = useState({});
  const [confidence, setConfidence] = useState(0.75);
  const [confidenceInput, setConfidenceInput] = useState("0.75");
  const [showConfidenceInfo, setShowConfidenceInfo] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_SERVER_URI}/api/packages/${encodeURIComponent(caseName)}`)
      .then((res) => {
        setCaseData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching package:', err);
        setLoading(false);
      });
  }, [caseName]);

  useEffect(() => {
    setConfidenceInput(confidence.toFixed(2));
  }, [confidence]);

  const calculateCaseCounts = () => {
    const conf = parseFloat(confidence);
    if (!caseData || isNaN(conf) || conf <= 0 || conf >= 1) return;

    const counts = {};
    caseData.contents.contents.forEach((skin) => {
      const count = getCaseCount(skin.odds, conf);
      counts[skin.name] = count;
    });

    setCaseCounts(counts);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-xl text-gray-300">Loading package details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!caseData) return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-red-400 mb-2">Package Not Found</h2>
        <p className="text-gray-400">The requested package could not be found.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
      {/* Header Section */}
      <div className="relative pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            {/* Package Image */}
            <div className="flex-shrink-0">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-full blur-xl"></div>
                <img 
                  src={caseData.imageUrl} 
                  alt={caseData.name} 
                  className="w-48 h-48 object-contain relative z-10"
                />
              </div>
            </div>

            {/* Package Info */}
            <div className="flex-1 text-center lg:text-left">
              <h1 className="text-4xl md:text-5xl font-black mb-4 bg-gradient-to-r from-orange-400 via-red-500 to-pink-500 bg-clip-text text-transparent">
                {caseData.name}
              </h1>
              
              {/* Price Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 border border-gray-700/50">
                  <div className="text-sm text-gray-400 mb-1">Package Price</div>
                  <div className="text-2xl font-bold text-green-400">${caseData.casePrice.toFixed(2)}</div>
                </div>
                <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 border border-gray-700/50">
                  <div className="text-sm text-gray-400 mb-1">Total Cost</div>
                  <div className="text-2xl font-bold text-orange-400">${(caseData.casePrice + caseData.priceToOpen).toFixed(2)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Calculator Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6 mb-8">
          <h2 className="text-2xl font-bold mb-6 text-center">Odds Calculator</h2>
          
          {/* Confidence Input */}
          <div className="max-w-md mx-auto space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-gray-300 font-medium">Confidence Level</label>
              <button
                onClick={() => setShowConfidenceInfo(!showConfidenceInfo)}
                className="text-orange-400 hover:text-orange-300 text-sm underline"
              >
                What is this?
              </button>
            </div>
            
            {showConfidenceInfo && (
              <div className="bg-gray-900/50 rounded-lg p-4 text-sm text-gray-300">
                <p className="mb-2">Confidence refers to how certain you want to be about getting a specific skin.</p>
                <ul className="space-y-1 text-xs">
                  <li>• 50% confidence = 50/50 chance you'll get the skin</li>
                  <li>• 95% confidence = almost certain, but requires more packages</li>
                </ul>
              </div>
            )}

            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="0.01"
                  max="0.99"
                  step="0.01"
                  value={confidence}
                  onChange={(e) => setConfidence(parseFloat(e.target.value))}
                  className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                />
                <input
                  type="text"
                  value={confidenceInput}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^[0-9.]*$/.test(val)) {
                      setConfidenceInput(val);
                    }
                  }}
                  onBlur={() => {
                    let val = parseFloat(confidenceInput);
                    if (isNaN(val) || val < 0.01) val = 0.01;
                    if (val > 0.99) val = 0.99;
                    setConfidence(val);
                  }}
                  className="w-20 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-center text-white"
                />
                <span className="text-sm text-gray-400 min-w-[80px]">
                  ({(confidence * 100).toFixed(0)}%)
                </span>
              </div>
            </div>

            <button
              onClick={calculateCaseCounts}
              className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 transform hover:scale-105"
            >
              Calculate Odds
            </button>
          </div>
        </div>

        {/* Results Grid */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6">
            <h3 className="text-xl font-bold mb-6 text-center">Souvenir Skins</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {caseData.contents.contents.map((skin, i) => (
                <div
                  key={i}
                  className="bg-gray-900/50 rounded-lg p-4 border-l-4"
                  style={{ borderLeftColor: rarityColors[skin.rarity] }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-white">
                      {skin.name}
                    </h4>
                    <span className="text-sm px-2 py-1 rounded bg-gray-700/50">
                      {(skin.odds * 100).toFixed(2)}%
                    </span>
                  </div>
                  
                  {caseCounts[skin.name] && (
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Packages needed:</span>
                        <span className="text-orange-400 font-semibold">{caseCounts[skin.name]}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Total cost:</span>
                        <span className="text-green-400 font-semibold">
                          ${(caseCounts[skin.name] * (caseData.casePrice + caseData.priceToOpen)).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SouvenirPackageDetailPage;
